import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { PenTool, Trophy, Target, CheckCircle2, Flame, Circle, Sparkles, RefreshCw, Users, Search, FileText, KeyRound } from 'lucide-react'
import { motion } from 'framer-motion'
import { Button, Card, CardContent } from '@/components/ui'
import { MissionsPanel } from '@/components/missions'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import { useStudio } from '@/context/StudioContext'
import { UpgradeBanner } from '@/components/UpgradeBanner'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import { recalculateStoryWordCounts } from '@/services/storyService'
import { calculateBadgeProgress, loadChildStats, type BadgeProgress } from '@/services/badgeService'
import { getTodaysMission, type MissionStatus } from '@/services/missionService'
import { useTour, type TourStep } from '@/components/tour'
import { QuickWriteLauncher } from '@/components/quickWrite/QuickWriteLauncher'

const TOUR_STORAGE_KEY = 'inkwell-tour-completed'

const dashboardTourSteps: TourStep[] = [
  {
    target: '[data-tour="welcome"]',
    title: 'Welcome to Inkwell Jr! 🎉',
    content: 'This is your dashboard. Two paths from here.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="quick-write"]',
    title: 'Start writing — fast',
    content: 'Tap here for a one-prompt writing sprint. No pressure, no setup. Hit Save when you are done.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="activities"]',
    title: 'Writing Activities',
    content: 'Choose from different ways to write: free writing in My Stories, guided challenges, character building, and more!',
    placement: 'top',
  },
  {
    target: '[data-tour="challenge"]',
    title: 'Story Challenges',
    content: 'Start here! Challenges give you fun prompts and teach writing techniques step by step.',
    placement: 'right',
  },
  {
    target: '[data-tour="help"]',
    title: 'Need Help?',
    content: 'Visit the Help page anytime for guides, FAQs, and tips. Happy writing!',
    placement: 'left',
  },
]

export function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { activeWriter } = useFamily()
  const { showToast } = useToast()
  const { mode, limits } = useStudio()
  const { startTour, isActive: isTourActive } = useTour()

  const [missionsCompleted, setMissionsCompleted] = useState(0)
  const [totalWordsWritten, setTotalWordsWritten] = useState(activeWriter?.total_words_written || 0)
  const [earnedBadges, setEarnedBadges] = useState<BadgeProgress[]>([])
  const [nextBadge, setNextBadge] = useState<BadgeProgress | null>(null)
  const [todaysMission, setTodaysMission] = useState<MissionStatus | null>(null)

  // Load missions completed count, fix word counts, and calculate badges
  useEffect(() => {
    async function loadDashboardData() {
      if (!activeWriter || !isSupabaseConfigured()) return

      // Recalculate word counts to fix any stale data
      await recalculateStoryWordCounts(activeWriter.id)

      // Fetch fresh child data with updated word count
      const [missionsResult, childResult] = await Promise.all([
        supabase
          .from('mission_completions')
          .select('*', { count: 'exact', head: true })
          .eq('child_id', activeWriter.id),
        supabase
          .from('children')
          .select('total_words_written')
          .eq('id', activeWriter.id)
          .single(),
      ])

      setMissionsCompleted(missionsResult.count || 0)
      if (childResult.data) {
        setTotalWordsWritten(childResult.data.total_words_written)
      }

      // Load badge progress
      const stats = await loadChildStats(activeWriter.id)
      if (stats) {
        const { earned, nextUp } = calculateBadgeProgress(stats)
        setEarnedBadges(earned)
        setNextBadge(nextUp)
      }

      // Load today's mission status
      const missionStatus = await getTodaysMission(activeWriter.id, activeWriter.age_range)
      setTodaysMission(missionStatus)
    }

    loadDashboardData()
  }, [activeWriter])

  // Show celebration if just completed a mission
  useEffect(() => {
    if (searchParams.get('completed') === 'true') {
      showToast('Mission complete! Keep up the great work!', 'success')
      setSearchParams({})
    }
  }, [searchParams, setSearchParams, showToast])

  // Trigger tour on first visit
  useEffect(() => {
    const hasCompletedTour = localStorage.getItem(TOUR_STORAGE_KEY)
    const shouldStartTour = searchParams.get('tour') === 'start'

    if ((!hasCompletedTour || shouldStartTour) && !isTourActive && activeWriter) {
      // Delay to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        startTour(dashboardTourSteps)
        localStorage.setItem(TOUR_STORAGE_KEY, 'true')
        if (shouldStartTour) {
          setSearchParams({})
        }
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [activeWriter, startTour, isTourActive, searchParams, setSearchParams])

  // Get next milestone for gamified stats
  const getNextMilestone = (category: 'missions' | 'words' | 'streak', current: number) => {
    const milestones = {
      missions: [
        { target: 1, name: 'First Steps', emoji: '🌱' },
        { target: 5, name: 'Mission Master', emoji: '🎯' },
        { target: 10, name: 'Unstoppable', emoji: '🚀' },
        { target: 25, name: 'Legend', emoji: '👑' },
      ],
      words: [
        { target: 100, name: 'Wordsmith', emoji: '✏️' },
        { target: 500, name: 'Storyteller', emoji: '📖' },
        { target: 1000, name: 'Author', emoji: '📚' },
        { target: 5000, name: 'Novelist', emoji: '🏆' },
      ],
      streak: [
        { target: 3, name: 'On a Roll', emoji: '🔥' },
        { target: 7, name: 'Week Warrior', emoji: '⚡' },
        { target: 14, name: 'Dedicated', emoji: '💪' },
      ],
    }

    const categoryMilestones = milestones[category]
    // Find the next milestone that hasn't been reached
    const next = categoryMilestones.find((m) => current < m.target)
    // If all milestones reached, return the last one
    return next || categoryMilestones[categoryMilestones.length - 1]
  }

  const currentStreak = activeWriter?.current_streak_days || 0

  // Get encouraging message for next badge
  const getEncouragingMessage = (badge: BadgeProgress): string => {
    const remaining = badge.badge.requirement - badge.progress
    const unitMap: Record<string, string> = {
      missions: 'mission',
      words: 'word',
      streak: 'day',
      mastery: 'skill',
    }
    const unit = unitMap[badge.badge.category] || 'step'
    const plural = remaining !== 1 ? 's' : ''
    return `Only ${remaining} ${unit}${plural} away from ${badge.badge.name}!`
  }

  const stats = [
    {
      label: 'Missions Done',
      value: missionsCompleted.toString(),
      icon: CheckCircle2,
      color: 'var(--color-kids-success)',
      current: missionsCompleted,
      milestone: getNextMilestone('missions', missionsCompleted),
    },
    {
      label: 'Words Written',
      value: totalWordsWritten.toLocaleString(),
      icon: PenTool,
      color: 'var(--color-kids-primary)',
      current: totalWordsWritten,
      milestone: getNextMilestone('words', totalWordsWritten),
    },
    {
      label: 'Day Streak',
      value: currentStreak.toString(),
      icon: currentStreak > 0 ? Flame : Target,
      color: currentStreak > 0 ? 'var(--color-kids-secondary)' : 'var(--color-kids-accent)',
      current: currentStreak,
      milestone: getNextMilestone('streak', currentStreak),
    },
  ]

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Upgrade banner for local mode */}
        {mode === 'local' && (
          <div className="mb-6">
            <UpgradeBanner />
          </div>
        )}

        {/* Welcome section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
          data-tour="welcome"
        >
          <h1 className="font-display text-[var(--font-size-kid-2xl)] font-bold text-[var(--color-kids-text)] mb-2">
            Hey, {activeWriter?.display_name}! 👋
          </h1>
          <p className="text-[var(--color-kids-text-muted)] text-lg">
            Ready to write something amazing today?
          </p>
        </motion.div>

        <QuickWriteLauncher />

        {/* Today's Journey - daily progress tracker */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-8"
          data-tour="stats"
        >
          <Card className="border-[var(--color-kids-primary)]/10">
            <CardContent className="p-4 sm:p-5">
              <p className="text-xs font-semibold text-[var(--color-kids-primary)] uppercase tracking-wide mb-3">
                Today's Journey
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                {/* Step 1: Streak */}
                <Link
                  to="/stories"
                  className="flex items-center gap-2 group"
                >
                  {currentStreak > 0 ? (
                    <CheckCircle2 className="w-5 h-5 text-[var(--color-kids-success)]" />
                  ) : (
                    <Circle className="w-5 h-5 text-[var(--color-kids-text-muted)]" />
                  )}
                  <span className={`text-sm group-hover:text-[var(--color-kids-primary)] transition-colors ${
                    currentStreak > 0 ? 'text-[var(--color-kids-text)]' : 'text-[var(--color-kids-text-muted)]'
                  }`}>
                    {currentStreak > 0 ? `${currentStreak}-day streak! 🔥` : 'Start your streak'}
                  </span>
                </Link>

                <span className="hidden sm:block text-[var(--color-kids-text-light)]">→</span>

                {/* Step 2: Mission */}
                <Link
                  to="/stories"
                  className="flex items-center gap-2 group"
                >
                  {todaysMission?.isCompleted ? (
                    <CheckCircle2 className="w-5 h-5 text-[var(--color-kids-success)]" />
                  ) : (
                    <Circle className="w-5 h-5 text-[var(--color-kids-text-muted)]" />
                  )}
                  <span className={`text-sm group-hover:text-[var(--color-kids-primary)] transition-colors ${
                    todaysMission?.isCompleted ? 'text-[var(--color-kids-text)]' : 'text-[var(--color-kids-text-muted)]'
                  }`}>
                    {todaysMission?.isCompleted
                      ? 'Mission complete! ✓'
                      : todaysMission?.isStarted
                        ? 'Continue your mission'
                        : "Complete today's mission"}
                  </span>
                </Link>

                <span className="hidden sm:block text-[var(--color-kids-text-light)]">→</span>

                {/* Step 3: Badge progress */}
                {nextBadge && (
                  <div className="flex items-center gap-2">
                    <span className="text-lg opacity-60">{nextBadge.badge.emoji}</span>
                    <span className="text-sm text-[var(--color-kids-text-muted)]">
                      {nextBadge.badge.requirement - nextBadge.progress} to {nextBadge.badge.name}
                    </span>
                  </div>
                )}
              </div>

              {/* Progress indicator */}
              {limits.canAccessBadges && (
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 h-1 bg-[var(--color-kids-surface-alt)] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-[var(--color-kids-success)] to-[var(--color-kids-primary)] rounded-full"
                      initial={{ width: 0 }}
                      animate={{
                        width: `${Math.round(
                          ((currentStreak > 0 ? 1 : 0) + (todaysMission?.isCompleted ? 1 : 0)) / 2 * 100
                        )}%`,
                      }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <span className="text-xs text-[var(--color-kids-text-muted)]">
                    {(currentStreak > 0 ? 1 : 0) + (todaysMission?.isCompleted ? 1 : 0)}/2
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
          {stats.map((stat, index) => {
            const progress = Math.min((stat.current / stat.milestone.target) * 100, 100)
            const isCompleted = stat.current >= stat.milestone.target

            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ background: `${stat.color}20` }}
                      >
                        <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[var(--color-kids-text-muted)]">{stat.label}</p>
                        <p className="font-display text-2xl font-bold text-[var(--color-kids-text)]">
                          {stat.value}
                        </p>
                        {/* Progress toward next milestone */}
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 h-1.5 bg-[var(--color-kids-surface-alt)] rounded-full overflow-hidden">
                            <motion.div
                              className="h-full rounded-full"
                              style={{ backgroundColor: stat.color }}
                              initial={{ width: 0 }}
                              animate={{ width: `${progress}%` }}
                              transition={{ duration: 0.5, delay: index * 0.1 }}
                            />
                          </div>
                          <span
                            className="text-sm"
                            title={isCompleted ? `${stat.milestone.name} earned!` : `${stat.current}/${stat.milestone.target} to ${stat.milestone.name}`}
                          >
                            {stat.milestone.emoji}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )
          })}
        </div>

        {/* Badges section - elevated, prominent placement */}
        {limits.canAccessBadges && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-[var(--color-kids-warning)]/5 to-[var(--color-kids-accent)]/5 border-[var(--color-kids-warning)]/20">
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  {/* Left: badges earned */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-[var(--color-kids-warning)]" />
                      <span className="font-display font-bold text-[var(--color-kids-text)]">
                        {earnedBadges.length} Badge{earnedBadges.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {earnedBadges.length > 0 && (
                      <div className="flex -space-x-1">
                        {earnedBadges.slice(0, 5).map(({ badge }) => (
                          <span
                            key={badge.id}
                            className="text-lg"
                            title={badge.name}
                          >
                            {badge.emoji}
                          </span>
                        ))}
                        {earnedBadges.length > 5 && (
                          <span className="text-sm text-[var(--color-kids-text-muted)] pl-2">
                            +{earnedBadges.length - 5}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right: next badge with encouraging message */}
                  {nextBadge && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-[var(--color-kids-text)] hidden sm:inline">
                        {getEncouragingMessage(nextBadge)}
                      </span>
                      <span className="text-xl opacity-60">{nextBadge.badge.emoji}</span>
                      <div className="flex-1 sm:flex-none sm:w-20 h-2 bg-[var(--color-kids-surface-alt)] rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-[var(--color-kids-warning)] rounded-full"
                          initial={{ width: 0 }}
                          animate={{
                            width: `${Math.min((nextBadge.progress / nextBadge.badge.requirement) * 100, 100)}%`,
                          }}
                          transition={{ duration: 0.5 }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Secondary doors: keep continuity for returning users */}
        <div className="mb-6">
          <p className="text-xs font-semibold text-[var(--color-kids-text-muted)] uppercase tracking-wide mb-3">
            Or keep going with
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link to="/stories" className="block">
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-center gap-3">
                  <PenTool className="w-5 h-5 text-[var(--color-kids-primary)] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-[var(--color-kids-text)]">My Stories</h3>
                    <p className="text-xs text-[var(--color-kids-text-muted)]">
                      {totalWordsWritten.toLocaleString()} words written
                      {currentStreak > 0 && ` · 🔥 ${currentStreak}-day streak`}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/storytelling" className="block">
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-center gap-3">
                  <KeyRound className="w-5 h-5" style={{ color: 'var(--color-story-primary)' }} />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-[var(--color-kids-text)]">Story Secrets</h3>
                    <p className="text-xs text-[var(--color-kids-text-muted)]">
                      Discover how stories work
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>

        {/* Today's Mission (full width below) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mt-6"
        >
          <MissionsPanel />
        </motion.div>

        {/* Learning Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mt-8"
          data-tour="activities"
        >
          <h2 className="font-display text-lg font-bold text-[var(--color-kids-text)] mb-4">
            Practice & Learn
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {/* Writing Studio */}
            <Link to="/studio">
              <Card className="h-full hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer group border-2 border-[var(--color-kids-primary)]/20">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[var(--color-kids-primary)]/10 flex items-center justify-center mb-3 group-hover:bg-[var(--color-kids-primary)]/20 transition-colors">
                    <FileText className="w-6 h-6 text-[var(--color-kids-primary)]" />
                  </div>
                  <h3 className="font-display font-bold text-[var(--color-kids-text)] mb-1">
                    Writing Studio
                  </h3>
                  <p className="text-xs text-[var(--color-kids-text-muted)]">
                    Your creative workspace
                  </p>
                </CardContent>
              </Card>
            </Link>

            {/* Challenge Lab */}
            <Link to="/challenge" data-tour="challenge">
              <Card className="h-full hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer group">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[var(--color-kids-primary)]/10 flex items-center justify-center mb-3 group-hover:bg-[var(--color-kids-primary)]/20 transition-colors">
                    <Sparkles className="w-6 h-6 text-[var(--color-kids-primary)]" />
                  </div>
                  <h3 className="font-display font-bold text-[var(--color-kids-text)] mb-1">
                    Challenge Lab
                  </h3>
                  <p className="text-xs text-[var(--color-kids-text-muted)]">
                    Writing missions with feedback
                  </p>
                </CardContent>
              </Card>
            </Link>

            {/* Revision Studio */}
            <Link to="/revise">
              <Card className="h-full hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer group">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[var(--color-kids-secondary)]/10 flex items-center justify-center mb-3 group-hover:bg-[var(--color-kids-secondary)]/20 transition-colors">
                    <RefreshCw className="w-6 h-6 text-[var(--color-kids-secondary)]" />
                  </div>
                  <h3 className="font-display font-bold text-[var(--color-kids-text)] mb-1">
                    Revision Studio
                  </h3>
                  <p className="text-xs text-[var(--color-kids-text-muted)]">
                    Make stories even better
                  </p>
                </CardContent>
              </Card>
            </Link>

            {/* Character Builder */}
            <Link to="/characters">
              <Card className="h-full hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer group">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[var(--color-kids-accent)]/10 flex items-center justify-center mb-3 group-hover:bg-[var(--color-kids-accent)]/20 transition-colors">
                    <Users className="w-6 h-6 text-[var(--color-kids-accent)]" />
                  </div>
                  <h3 className="font-display font-bold text-[var(--color-kids-text)] mb-1">
                    Characters
                  </h3>
                  <p className="text-xs text-[var(--color-kids-text-muted)]">
                    Create reusable characters
                  </p>
                </CardContent>
              </Card>
            </Link>

            {/* Story Detective */}
            <Link to="/detective">
              <Card className="h-full hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer group">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[var(--color-kids-success)]/10 flex items-center justify-center mb-3 group-hover:bg-[var(--color-kids-success)]/20 transition-colors">
                    <Search className="w-6 h-6 text-[var(--color-kids-success)]" />
                  </div>
                  <h3 className="font-display font-bold text-[var(--color-kids-text)] mb-1">
                    Story Detective
                  </h3>
                  <p className="text-xs text-[var(--color-kids-text-muted)]">
                    Read like a writer
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </motion.div>

        {/* Upgrade prompt for local mode (badges not accessible) */}
        {!limits.canAccessBadges && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8"
          >
            <Card>
              <CardContent className="p-6 text-center">
                <Trophy className="w-8 h-8 text-[var(--color-kids-warning)] mx-auto mb-3 opacity-50" />
                <p className="text-[var(--color-kids-text-muted)] mb-4">
                  Upgrade to Cloud Studio to track your badges, progress, and achievements!
                </p>
                <Link to="/upgrade">
                  <Button variant="secondary" size="sm">
                    Learn More
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        )}
    </div>
  )
}
